import { useMemo, useRef, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

/**
 * Labels als losse chips in plaats van een komma-string.
 *
 * Een tekstveld met komma's verbergt twee dingen: welke labels er al bestaan, en
 * of je er per ongeluk een nieuwe aanmaakt met een andere schrijfwijze. Hier zie
 * je de bestaande labels, matcht toevoegen hoofdletterongevoelig op wat er al is,
 * en is een nieuw label een zichtbare keuze.
 */
interface LabelInputProps {
  value: string[];
  onChange: (value: string[]) => void;
  existing: string[];
  id?: string;
}

export function LabelInput({ value, onChange, existing, id }: LabelInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  const needle = draft.trim().toLowerCase();
  const selected = useMemo(
    () => new Set(value.map((label) => label.toLowerCase())),
    [value],
  );

  const suggestions = useMemo(
    () =>
      existing
        .filter((label) => !selected.has(label.toLowerCase()))
        .filter((label) => (needle ? label.toLowerCase().includes(needle) : true))
        .slice(0, 8),
    [existing, needle, selected],
  );

  const exactExists = existing.some((label) => label.toLowerCase() === needle);
  const showCreate = Boolean(needle) && !exactExists && !selected.has(needle);
  const options = showCreate ? [...suggestions, `__new__${draft.trim()}`] : suggestions;

  const add = (raw: string) => {
    const label = raw.startsWith('__new__') ? raw.slice('__new__'.length) : raw;
    const clean = label.trim();
    if (!clean || selected.has(clean.toLowerCase())) return;

    // Schrijf een bestaand label altijd op de manier waarop het al bestaat,
    // zodat "Recurring" en "recurring" niet twee labels worden.
    const canonical = existing.find((entry) => entry.toLowerCase() === clean.toLowerCase());
    onChange([...value, canonical ?? clean]);
    setDraft('');
    setHighlight(0);
  };

  const remove = (label: string) => onChange(value.filter((entry) => entry !== label));

  const unusedSuggestions = existing
    .filter((label) => !selected.has(label.toLowerCase()))
    .slice(0, 6);

  return (
    <div className="space-y-1.5">
      <div
        className="flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border border-input bg-transparent px-2 py-1.5 focus-within:ring-1 focus-within:ring-ring"
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((label) => (
          <Badge key={label} variant="secondary" className="gap-1 pr-1 font-normal">
            {label}
            <button
              type="button"
              aria-label={`Label ${label} verwijderen`}
              onClick={(event) => {
                event.stopPropagation();
                remove(label);
              }}
              className="rounded-sm text-muted-foreground hover:text-foreground"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}

        <div className="relative min-w-[8rem] flex-1">
          <input
            id={id}
            ref={inputRef}
            value={draft}
            placeholder={value.length ? '' : 'Kies of typ een label'}
            className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            onChange={(event) => {
              const next = event.target.value;
              // Een komma of puntkomma bevestigt, zoals in het oude veld.
              if (/[,;]/.test(next)) {
                next.split(/[,;]/).forEach((part) => part.trim() && add(part));
                return;
              }
              setDraft(next);
              setOpen(true);
              setHighlight(0);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => window.setTimeout(() => setOpen(false), 120)}
            onKeyDown={(event) => {
              if (event.key === 'Backspace' && !draft && value.length) {
                remove(value[value.length - 1]);
                return;
              }
              if (!options.length) {
                if (event.key === 'Enter' && draft.trim()) {
                  event.preventDefault();
                  add(draft);
                }
                return;
              }
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                setHighlight((current) => (current + 1) % options.length);
              } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                setHighlight((current) => (current - 1 + options.length) % options.length);
              } else if (event.key === 'Enter' || event.key === 'Tab') {
                if (event.key === 'Tab' && !draft.trim()) return;
                event.preventDefault();
                add(options[highlight]);
              } else if (event.key === 'Escape') {
                setOpen(false);
              }
            }}
          />

          {open && options.length > 0 && (
            <ul className="absolute z-50 mt-2 max-h-56 w-64 overflow-y-auto rounded-md border bg-popover shadow-md">
              {options.map((option, index) => {
                const isNew = option.startsWith('__new__');
                const label = isNew ? option.slice('__new__'.length) : option;
                return (
                  <li key={option}>
                    <button
                      type="button"
                      tabIndex={-1}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => add(option)}
                      onMouseEnter={() => setHighlight(index)}
                      className={cn(
                        'flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm',
                        index === highlight && 'bg-accent',
                      )}
                    >
                      {isNew ? (
                        <>
                          <Plus className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          <span>
                            Nieuw label <strong>{label}</strong>
                          </span>
                        </>
                      ) : (
                        <span className="truncate">{label}</span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {unusedSuggestions.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] text-muted-foreground">In gebruik:</span>
          {unusedSuggestions.map((label) => (
            <button key={label} type="button" onClick={() => add(label)}>
              <Badge
                variant="outline"
                className="cursor-pointer font-normal text-[11px] hover:bg-accent"
              >
                {label}
              </Badge>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default LabelInput;
